import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

let nextId = 1;

const TOAST_CONFIG = {
  success: { background: '#10b981', icon: '✓' },
  warning: { background: '#f59e0b', icon: '!' },
  error:   { background: '#f43f5e', icon: '✕' },
};

function ToastContainer({ onRemove, toasts }) {
  if (!toasts.length) return null;

  return (
    <div className="toast-container">
      {toasts.map((item) => {
        const { background, icon } = TOAST_CONFIG[item.type] || TOAST_CONFIG.error;
        return (
          <div key={item.id} className="toast-item" style={{ background }}>
            <span className="toast-icon">{icon}</span>
            <span className="toast-message">{item.message}</span>
            <button
              className="toast-close"
              type="button"
              aria-label="Dismiss"
              onClick={() => onRemove(item.id)}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const toast = useMemo(() => ({
    success: (message) => addToast('success', message),
    warning: (message) => addToast('warning', message),
    error:   (message) => addToast('error', message),
  }), [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
